import type React from 'react';
import { Icon } from '@iconify/react';
import { Card, Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { TONE_TEXT, VERDICT_SURFACE, type OverviewTone } from '../overviewMeta';

type OverviewCardProps = {
    /** Literal testid prefix for this card, e.g. `net-working-capital`. */
    slot: string;
    icon: string;
    /** The card's verdict tone, or `null` while it has nothing to assess. */
    tone: OverviewTone | null;
    title: string;
    question: string;
    /** Movement since the previous period. Absent in the empty state. */
    delta?: { label: string; tone: OverviewTone } | null;
    /** The measured body: rows, a bar, or the empty state's prompt. */
    children: React.ReactNode;
    /**
     * The one-line answer, in a tinted band. `accent` is for a figure that is
     * a reading rather than a judgement — a coverage ratio is neither good nor
     * bad until you know the threshold.
     */
    verdict?: {
        label: string;
        value: string;
        tone: OverviewTone | 'accent';
        icon?: string;
    } | null;
};

/**
 * The frame all four overview cards share: what the card measures, how it
 * moved, the measurement, and the answer.
 *
 * One frame rather than four, because the cards are read as a set — a reader
 * scanning them relies on the question sitting in the same place every time,
 * and on the tinted band at the bottom always being the answer. Only the middle
 * differs, which is what `children` is for.
 *
 * The question stays in every state, including empty. It is what makes a card
 * with no figure still legible, and it means nothing moves when the figure
 * arrives.
 */
export const OverviewCard: React.FC<OverviewCardProps> = ({
    slot,
    icon,
    tone,
    title,
    question,
    delta,
    children,
    verdict,
}) => (
    <Card {...getNodeTestIdProp(`${slot}-card`)}>
        <Card.Header>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-tertiary">
                        <Icon
                            aria-hidden
                            className={tone ? TONE_TEXT[tone] : 'text-muted'}
                            icon={icon}
                            width={18}
                        />
                    </span>
                    <div className="flex flex-col gap-1">
                        <Card.Title>{title}</Card.Title>
                        <Card.Description>{question}</Card.Description>
                    </div>
                </div>
                {delta ? (
                    <Chip
                        color={delta.tone}
                        {...getNodeTestIdProp(`${slot}-delta`)}
                    >
                        <Chip.Label>{delta.label}</Chip.Label>
                    </Chip>
                ) : null}
            </div>
        </Card.Header>
        <Card.Content className="flex flex-col gap-3">{children}</Card.Content>
        {verdict ? (
            <Card.Footer>
                <div
                    className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 ${VERDICT_SURFACE[verdict.tone]}`}
                    {...getNodeTestIdProp(`${slot}-verdict`)}
                >
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                        {verdict.icon ? (
                            <Icon aria-hidden icon={verdict.icon} />
                        ) : null}
                        {verdict.label}
                    </span>
                    <span className="text-lg font-semibold">
                        {verdict.value}
                    </span>
                </div>
            </Card.Footer>
        ) : null}
    </Card>
);
